import { formatHumanName, getExtension, isResource } from '@medplum/core';
import { Coding, Communication, Organization, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import {
  BARIENDO_CT_SYSTEM,
  COMMUNICATION_MAIN_CATEGORIES,
  CommunicationCategory,
  CommunicationDirection,
  CommunicationMedium,
  DOCUMENT_ATTACHMENT_PAYLOAD_ID,
  RICH_TEXT_CONTENT_PAYLOAD_ID,
  TEXT_CONTENT_PAYLOAD_ID,
  VIRTUAL_RECEPTIONIST_PAYLOAD_ID,
  VOICE_RECORDING_PAYLOAD_ID,
} from './constants';
import {
  FAX_ORIGINATING_NUMBER,
  FROM_PHONE_NUMBER_COMMUNICATION_EXTENSION,
  INCOMING_CALL_ROUTING_HISTORY_EXTENSION,
  TO_PHONE_NUMBER_COMMUNICATION_EXTENSION,
} from './extensions';
import { FAX_COMMUNICATION_CATEGORY_INBOUND, FAX_COMMUNICATION_CATEGORY_OUTBOUND } from './codings';
import { isProviderRoutingHistoryEntry } from './call-routing-history';
import { hasCode, isReferenceOf } from './services';
import { deriveVoiceCallDisplayStatus, isUnansweredVoiceCallDisplayStatus } from './voice-call-outcome';

export const CommunicationType = {
  VOICE_CALL_INCOMING: 'voice-call-incoming',
  VOICE_CALL_OUTGOING: 'voice-call-outgoing',
  VOICE_CALL_FAILED: 'voice-call-failed',
  TEXT_MESSAGE_INCOMING: 'text-message-incoming',
  TEXT_MESSAGE_OUTGOING: 'text-message-outgoing',
  TEXT_MESSAGE: 'text-message',
  TIMELINE_NOTE: 'timeline-note',
  STANDALONE_NOTE: 'standalone-note',
  CHAT_MESSAGE_INCOMING: 'chat-message-incoming',
  CHAT_MESSAGE_OUTGOING: 'chat-message-outgoing',
  CHAT_MESSAGE: 'chat-message',
  UNKNOWN: 'unknown',
  FAX_INCOMING: 'fax-incoming',
  FAX_OUTGOING: 'fax-outgoing',
} as const;

export type CommunicationType = (typeof CommunicationType)[keyof typeof CommunicationType];

export function getCommunicationCategory(communication: Communication): CommunicationCategory | undefined {
  return communication.category
    ?.flatMap((category) => category.coding)
    .find((coding) => COMMUNICATION_MAIN_CATEGORIES.includes(coding?.code as CommunicationCategory))
    ?.code as CommunicationCategory;
}

export function isForwardedCommunication(communication: Communication): boolean {
  return !!(
    hasCategory(communication, CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD) ||
    hasCategory(communication, CommunicationCategory.SALES_TEAM_FORWARD)
  );
}

export function hasCategory(communication: Communication, category: CommunicationCategory) {
  return communication.category?.some(hasCode(category));
}

export function isIncomingCallCommunication(communication: Communication): boolean {
  return (
    getCommunicationCategory(communication) === CommunicationCategory.PHONE_CALL &&
    isIncomingCommunication(communication)
  );
}

type FaxCheckable = {
  category?: Array<{ coding?: Coding[] }>;
  medium?: Array<{ coding?: Coding[] }>;
};

function isMedplumInboundFaxDirection(communication?: FaxCheckable): boolean {
  return Boolean(
    communication?.category?.some((cat) =>
      cat.coding?.some(
        (c) =>
          c.system === FAX_COMMUNICATION_CATEGORY_INBOUND.system && c.code === FAX_COMMUNICATION_CATEGORY_INBOUND.code
      )
    )
  );
}

function isMedplumOutboundFaxDirection(communication?: FaxCheckable): boolean {
  return Boolean(
    communication?.category?.some((cat) =>
      cat.coding?.some(
        (c) =>
          c.system === FAX_COMMUNICATION_CATEGORY_OUTBOUND.system && c.code === FAX_COMMUNICATION_CATEGORY_OUTBOUND.code
      )
    )
  );
}

export function isMedplumFaxCategory(communication?: FaxCheckable): boolean {
  return isMedplumInboundFaxDirection(communication) || isMedplumOutboundFaxDirection(communication);
}

/**
 * Checks if a communication is an inbound fax (Bariendo `inbound` category and/or Medplum fax-direction).
 * Works with both Communication (FHIR resource) and CommunicationFragment (GraphQL fragment).
 */
export function isFaxCommunication(communication?: FaxCheckable): boolean {
  if (!communication) {
    return false;
  }
  const hasFaxMedium = communication.medium?.some(hasCode(CommunicationMedium.FAX));
  if (!hasFaxMedium) {
    return false;
  }
  if (isMedplumInboundFaxDirection(communication)) {
    return true;
  }
  return Boolean(communication.category?.some(hasCode(CommunicationCategory.INBOUND)));
}

/**
 * Whether this Communication maps to a fax row in the patient timeline (`timelineGroups`).
 * Uses the first `medium` entry only, matching `mapResourceToPatientTimelineCommunication`.
 */
export function isTimelinePatientFaxCommunication(communication: Communication | undefined): boolean {
  if (!communication) {
    return false;
  }
  const medium = communication.medium?.[0]?.coding?.[0]?.code;
  const isFaxMedium = medium === CommunicationMedium.FAX;
  if (!isFaxMedium) {
    return false;
  }
  return (
    isFaxCommunication(communication) ||
    hasCategory(communication, CommunicationCategory.FAX_MESSAGE) ||
    isMedplumFaxCategory(communication)
  );
}

export function getCommunicationType(communication: Communication | undefined): CommunicationType {
  if (!communication) {
    return CommunicationType.UNKNOWN;
  }

  const category = getCommunicationCategory(communication);
  const direction = getCommunicationDirection(communication);

  // Check for voice call
  const hasVoiceCallCategory = category === CommunicationCategory.PHONE_CALL;
  const hasPhoneCallMedium = communication.medium?.some((medium) =>
    medium.coding?.some((coding: Coding) => coding.code === CommunicationMedium.PHONE_CALL)
  );

  if (hasVoiceCallCategory && hasPhoneCallMedium) {
    // Outcome first, then direction. FHIR status describes the *record*, not
    // the call: a missed inbound call completes normally, so keying off
    // `status !== 'completed'` gave every missed call the ordinary
    // incoming-call icon and reserved the missed-call one for the three
    // statuses (`stopped` / `not-done` / `entered-in-error`) telephony almost
    // never writes.
    if (isUnansweredVoiceCallDisplayStatus(deriveVoiceCallDisplayStatus(communication))) {
      return CommunicationType.VOICE_CALL_FAILED;
    }

    if (direction === CommunicationDirection.INCOMING) {
      return CommunicationType.VOICE_CALL_INCOMING;
    } else if (direction === CommunicationDirection.OUTGOING) {
      return CommunicationType.VOICE_CALL_OUTGOING;
    }

    return CommunicationType.VOICE_CALL_INCOMING; // Default fallback for voice calls
  }

  // Check for SMS/text message
  const hasTextMessageCategory = category === CommunicationCategory.SMS_MESSAGE;
  const hasSmsMedium = communication.medium?.some((medium) =>
    medium.coding?.some((coding: Coding) => coding.code === CommunicationMedium.SMS)
  );

  if (hasTextMessageCategory && hasSmsMedium) {
    // For text messages, differentiate by direction like voice calls
    if (direction === CommunicationDirection.INCOMING) {
      return CommunicationType.TEXT_MESSAGE_INCOMING;
    } else if (direction === CommunicationDirection.OUTGOING) {
      return CommunicationType.TEXT_MESSAGE_OUTGOING;
    }

    return CommunicationType.TEXT_MESSAGE;
  }

  const hasChatCategory = category === CommunicationCategory.CHAT;
  if (hasChatCategory) {
    if (direction === CommunicationDirection.INCOMING) {
      return CommunicationType.CHAT_MESSAGE_INCOMING;
    } else if (direction === CommunicationDirection.OUTGOING) {
      return CommunicationType.CHAT_MESSAGE_OUTGOING;
    }
    return CommunicationType.CHAT_MESSAGE;
  }

  // Check for timeline note
  const hasTimelineNoteCategory = category === CommunicationCategory.TIMELINE_NOTE;

  if (hasTimelineNoteCategory) {
    return CommunicationType.TIMELINE_NOTE;
  }

  const hasStandaloneNoteCategory = category === CommunicationCategory.STANDALONE_NOTE;

  if (hasStandaloneNoteCategory) {
    return CommunicationType.STANDALONE_NOTE;
  }

  const hasFaxMedium = communication.medium?.some((medium) =>
    medium.coding?.some((coding: Coding) => coding.code === CommunicationMedium.FAX)
  );

  if (hasFaxMedium) {
    if (isFaxCommunication(communication)) {
      return CommunicationType.FAX_INCOMING;
    }
    return CommunicationType.FAX_OUTGOING;
  }

  // Default fallback
  return CommunicationType.UNKNOWN;
}

export function getCommunicationDirection(communication: Communication): CommunicationDirection | undefined {
  const isIncoming = communication.category?.some((cat) =>
    cat.coding?.some((coding: Coding) => coding.code === CommunicationDirection.INCOMING)
  );

  if (isIncoming) return CommunicationDirection.INCOMING;

  const isOutgoing = communication.category?.some((cat) =>
    cat.coding?.some((coding: Coding) => coding.code === CommunicationDirection.OUTGOING)
  );

  if (isOutgoing) return CommunicationDirection.OUTGOING;

  if (isMedplumOutboundFaxDirection(communication)) {
    return CommunicationDirection.OUTGOING;
  }

  if (isMedplumInboundFaxDirection(communication) && communication.medium?.some(hasCode(CommunicationMedium.FAX))) {
    return CommunicationDirection.INCOMING;
  }

  if (isFaxCommunication(communication)) {
    return CommunicationDirection.INCOMING;
  }

  return undefined;
}

/** Any category coding on the resource matches one of the given codes (FHIR allows multiple category CodeableConcepts). */
export function communicationHasAnyCategoryCode(
  communication: { category?: Array<{ coding?: Coding[] }> } | undefined,
  codes: Iterable<string>
): boolean {
  const set = codes instanceof Set ? codes : new Set(codes);
  if (!communication?.category) {
    return false;
  }
  return communication.category.some((concept) =>
    concept.coding?.some((c) => Boolean(c.code && set.has(c.code)) && (!c.system || c.system === BARIENDO_CT_SYSTEM))
  );
}

/**
 * Best-effort fax line for display/search: originating number on inbound, to-number extension on outbound,
 * or remote party Organization display.
 */
export function getFaxCounterpartyLabel(communication: Communication): string {
  const direction = getCommunicationDirection(communication);
  const originating = communication.extension?.find((e) => e.url === FAX_ORIGINATING_NUMBER)?.valueString;
  if (direction === CommunicationDirection.INCOMING) {
    return originating?.trim() || getFromNumber(communication)?.trim() || '';
  }
  if (direction === CommunicationDirection.OUTGOING) {
    return getToNumber(communication)?.trim() || communication.recipient?.[0]?.display?.trim() || '';
  }
  return originating?.trim() || getToNumber(communication)?.trim() || getFromNumber(communication)?.trim() || '';
}

export function isIncomingCommunication(communication: Communication): boolean {
  return getCommunicationDirection(communication) === CommunicationDirection.INCOMING;
}

/**
 * Who a call or text was with, as the reference `send-sms-message` takes for `recipient`:
 * the patient when the row has one, else the partner organization on the external side of
 * the exchange, else nothing — an unknown number.
 *
 * `subject` wins outright; a partner call charted against a patient is that patient's. The
 * partner side is the *external* party, which is `sender` on an inbound row and
 * `recipient[0]` on an outbound one, and a Practitioner there is one of us, never a contact.
 * Only the reference is read, so this works on GraphQL rows whose `resource` branch was not
 * selected — the SMS log selects no Organization at all.
 */
export function getCommunicationContactReference(
  communication: Communication
): Reference<Patient | Organization> | undefined {
  if (isReferenceOf(communication.subject, 'Patient')) {
    return communication.subject as Reference<Patient>;
  }

  const external = isIncomingCommunication(communication) ? communication.sender : communication.recipient?.[0];

  return isReferenceOf(external, 'Organization') ? (external as Reference<Organization>) : undefined;
}

/**
 * Who a call or text was with, for a title: the patient's name, else the partner's, else
 * `undefined` for a number nothing matched.
 *
 * Reads the `resource` Medplum's GraphQL hangs off the very reference
 * {@link getCommunicationContactReference} picks, so the name and the reference can never
 * disagree about who the row is with, and a query that selected no `resource` branch gets
 * `undefined` rather than a guess.
 */
export function getCommunicationContactName(communication: Communication): string | undefined {
  const resource = getCommunicationContactReference(communication)?.resource;
  if (isResource(resource, 'Patient')) {
    return formatHumanName((resource as Patient).name?.[0]) || undefined;
  }
  if (isResource(resource, 'Organization')) {
    return (resource as Organization).name || undefined;
  }
  return undefined;
}

export function getFromNumber(communication: Communication): string | undefined {
  return communication.extension?.find((ext) => ext.url === FROM_PHONE_NUMBER_COMMUNICATION_EXTENSION)?.valueString;
}

export function getToNumber(communication: Communication): string | undefined {
  return communication.extension?.find((ext) => ext.url === TO_PHONE_NUMBER_COMMUNICATION_EXTENSION)?.valueString;
}

/**
 * The Everself-side number on a call or text — the one WE answered on or sent
 * from, the mirror image of {@link getExternalNumberFromPhoneCommunication}.
 *
 * Both numbers already ride on the Communication as extensions, so a card can
 * name the line a patient reached without a second read.
 */
export function getInternalNumberFromPhoneCommunication(communication: Communication): string | undefined {
  const direction = getCommunicationDirection(communication);

  if (direction === CommunicationDirection.INCOMING) {
    return getToNumber(communication);
  } else if (direction === CommunicationDirection.OUTGOING) {
    return getFromNumber(communication);
  }

  return undefined;
}

export function getExternalNumberFromPhoneCommunication(communication: Communication): string | undefined {
  const direction = getCommunicationDirection(communication);

  if (direction === CommunicationDirection.INCOMING) {
    return getFromNumber(communication);
  } else if (direction === CommunicationDirection.OUTGOING) {
    return getToNumber(communication);
  }

  return undefined;
}

/**
 * Every provider this call has been offered to — the closed routing-history
 * extension plus whoever it is ringing right now.
 *
 * `recipient` is read in full, not just `[0]`: a pool rung rings every eligible
 * provider at once (BAR-2622) and parks all of them on `recipient` until one
 * answers. Reading only the first would let the next rung ring someone whose
 * phone is already ringing on this same call.
 */
export function getProviderRoutingHistory(communication: Communication): Reference<Practitioner>[] {
  const recipients = communication.recipient ?? [];
  const callersHistory = getExtension(communication, INCOMING_CALL_ROUTING_HISTORY_EXTENSION)?.extension || [];

  // TODO: Add validation to ensure recipient is a Practitioner (not Patient/Organization)
  // For incoming calls, recipient should be Practitioner. For outgoing calls, recipient is Patient/Organization.
  // Add type guard check to prevent including Patient/Organization in routing history.
  //
  // The container also holds skip entries (BAR-2621), which carry a reason and no
  // practitioner. Mapping those in unfiltered would put `undefined` in this list, and
  // `isProviderInRoutingHistory` compares reference strings — an undefined entry would
  // match any provider whose reference could not be resolved, silently excluding them
  // from every remaining rung.
  return [
    ...callersHistory.filter(isProviderRoutingHistoryEntry).map((ext) => ext.valueReference as Reference<Practitioner>),
    ...(recipients as Reference<Practitioner>[]),
  ];
}

export function getFirstProviderInRoutingHistory(communication: Communication): Reference<Practitioner> | undefined {
  const callerHistory = getProviderRoutingHistory(communication);
  // TODO: Add validation to ensure recipient is a Practitioner (not Patient/Organization)
  // Same validation needed as in getProviderRoutingHistory
  const recipient = communication.recipient?.[0] as Reference<Practitioner> | undefined;
  return callerHistory?.[0] ?? recipient;
}

// Helper types for Communication payloads
export type CommunicationPayload = NonNullable<Communication['payload']>[number];

export type KnownPayloadId =
  | typeof VOICE_RECORDING_PAYLOAD_ID
  | typeof TEXT_CONTENT_PAYLOAD_ID
  | typeof RICH_TEXT_CONTENT_PAYLOAD_ID
  | typeof DOCUMENT_ATTACHMENT_PAYLOAD_ID
  | typeof VIRTUAL_RECEPTIONIST_PAYLOAD_ID;

/**
 * Finds the first payload entry in a Communication matching the provided payload id/type.
 * This helps avoid repeatedly writing `communication.payload?.find((p) => p.id === SOME_ID)`.
 * @param communication The Communication resource to search within.
 * @param payloadId The payload id/type to search for.
 * @returns The first payload entry matching the provided payload id/type, or undefined if not found.
 */
export function getPayloadByType(
  communication: Communication | undefined,
  payloadId: KnownPayloadId
): CommunicationPayload | undefined {
  return communication?.payload?.find((p) => p.id === payloadId);
}

/**
 * A rep reaching out: an outbound call or text sent by a Practitioner (BAR-2807).
 * Automated sends and inbound messages aren't attempts, and neither is a call
 * with no direction recorded.
 */
export function isContactAttempt(communication: Communication): boolean {
  return (
    communicationHasAnyCategoryCode(communication, [
      CommunicationCategory.PHONE_CALL,
      CommunicationCategory.SMS_MESSAGE,
    ]) &&
    getCommunicationDirection(communication) === CommunicationDirection.OUTGOING &&
    communication.sender?.reference?.split('/')[0] === 'Practitioner'
  );
}

/** Contact attempts sent on or after `since`. */
export function countContactAttempts(communications: Communication[] | undefined, since: string | undefined): number {
  const floor = since ? new Date(since).getTime() : undefined;
  return (communications ?? []).filter(
    (communication) =>
      isContactAttempt(communication) &&
      (floor === undefined || (communication.sent !== undefined && new Date(communication.sent).getTime() >= floor))
  ).length;
}
